import { FC, Fragment, useEffect, useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { CatalogEntryListCompareCollector } from '@/components/content/CatalogEntryList/parts/Compare/Collector';
import { CatalogEntryListFacetChips } from '@/components/content/CatalogEntryList/parts/FacetChips';
import { CatalogEntryListPagination } from '@/components/content/CatalogEntryList/parts/Pagination';
import { CatalogEntryListProductGrid } from '@/components/content/CatalogEntryList/parts/ProductGrid';
import { CatalogEntryListSort } from '@/components/content/CatalogEntryList/parts/Sort';
import { CategoryTabs } from '@/components/content/CatalogEntryList/parts/CategoryTabs';
import { catalogEntryListContainerSX } from '@/components/content/CatalogEntryList/styles/container';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useCompareCollector } from '@/data/Content/CompareCollector';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { CatalogEntryButtonFilterBy } from '@/components/content/CatalogEntryList/parts/ButtonFilterBy';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { CatalogEntryListEmptyContentRecommendation } from '@/components/content/CatalogEntryList/parts/CatalogEntryListEmptyContentRecommendation';
import { parseHTML } from '@/utils/parseHTML';
import { stripHtmlTags } from '@/utils/stripHtmlTags';
import { DpPersonalizationCampaign } from '@/components/blocks/DpPersonalizationCampaign';
import { PERSONALIZATION_CAROUSEL_ESPOT } from '@/data/Content/RepurchaseCoupon';

const sanitizeSearchTerm = (term: unknown) =>
	typeof term === 'string' ? stripHtmlTags(term) : Array.isArray(term) ? stripHtmlTags(term.join(' ')) : '';

// eslint-disable-next-line complexity
export const CatalogEntryList: FC<{ id: ID; isFilter?: boolean; isFilterChips?: boolean }> = ({
	id,
	isFilter,
	isFilterChips,
}) => {
	const catalogEntryList = useCatalogEntryList(id);
	const { entitled, loading, filteredParams, onTriggerMarketingEvent } = catalogEntryList;
	const { searchTerm } = filteredParams;
	const safeSearchTerm = sanitizeSearchTerm(searchTerm);
	const compare = useCompareCollector(id);
	const { compareEnabled, compareState } = compare;
	const contextValue = useMemo(
		() => ({ ...catalogEntryList, ...compare }),
		[catalogEntryList, compare]
	);

	const isMobile = useIsMobile();

	useEffect(() => {
		if (searchTerm) {
			onTriggerMarketingEvent(safeSearchTerm || searchTerm);
		}
	}, [searchTerm, safeSearchTerm, onTriggerMarketingEvent]);

	if (!loading && !entitled && !searchTerm) {
		return <CatalogEntryListEmptyContentRecommendation />;
	}
	if (isFilterChips) {
		return (
			<ContentProvider value={contextValue}>
				<CatalogEntryListFacetChips id={id} isFilter={true} />
			</ContentProvider>
		);
	}
	if (isFilter) {
		return (
			<ContentProvider value={contextValue}>
				<Typography
					variant={'h4'}
					sx={{ fontSize: '15px', marginBottom: '9px', fontWeight: 'bold' }}
				>
					{'Ordenar por'}
				</Typography>
				<CatalogEntryListSort />
			</ContentProvider>
		);
	}
	return (
		<ContentProvider value={contextValue}>
			<Stack
				sx={catalogEntryListContainerSX}
				id="catalog-entry-list-product-container"
				data-testid="catalog-entry-list-product-container"
			>
				{isMobile ? (
					<Fragment>
						{searchTerm ? (
							<Typography
								component={'p'}
								sx={{
									fontSize: '18px',
									marginBottom: '15px',
									mt: '20px',
									fontFamily: 'Open Sans, "Helvetica Neue", Arial, sans-serif',
									color: '#2c2c2c',
								}}
							>
								{`Resultados de búsqueda para `}
								<Typography
									component={'span'}
									sx={{
										color: '#2c2c2c',
										fontWeight: '800',
										textTransform: 'uppercase',
										fontSize: '17px',
										mx: '3px',
									}}
								>
									{` "${safeSearchTerm.toUpperCase()}" `}
								</Typography>
							</Typography>
						) : null}
					</Fragment>
				) : null}
				{isMobile ? <CatalogEntryListFacetChips id={id} /> : null}
				{isMobile ? <CategoryTabs /> : null}
				{!isMobile ? (
					<Fragment>
						{safeSearchTerm ? (
							<Typography
								component={'p'}
								sx={{
									fontSize: '18px',
									marginBottom: '15px',
									fontFamily: 'Open Sans, "Helvetica Neue", Arial, sans-serif',
									color: '#2c2c2c',
									mt: '55px',
									display: 'flex',
									alignItems: 'center',
								}}
							>
								{parseHTML(
									`Resultados de búsqueda para <p component="span" sx="{'fontWeight': '800', 'fontSize': '18px'}">&nbsp;"${safeSearchTerm.toUpperCase()}"&nbsp;</p> Encontramos (${
										loading ? '...' : (contextValue.total || 0)?.toLocaleString('es-MX')
									} coincidencias)`
								)}
							</Typography>
						) : null}
					</Fragment>
				) : null}
				{!safeSearchTerm ? <DpPersonalizationCampaign ems={PERSONALIZATION_CAROUSEL_ESPOT} /> : null}
				<Box
					sx={{
						borderTop: '1px solid #E1E1E1',
						pt: '16px',
						mb: '19px',
						display: { xs: 'block', md: 'flex' },
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					{!isMobile ? (
						<Box sx={{ overflow: { xs: 'scroll', md: 'visible' } }}>
							<CatalogEntryListFacetChips id={id} />
						</Box>
					) : null}
					<Box>{!isMobile ? <CatalogEntryListSort /> : <CatalogEntryButtonFilterBy />}</Box>

					{isMobile ? (
						<Box sx={{ overflow: { xs: 'scroll', md: 'visible' } }}>
							<CatalogEntryListFacetChips id={id} isFilter={'true'} />
						</Box>
					) : null}
				</Box>

				<CatalogEntryListProductGrid />

				<CatalogEntryListPagination />
			</Stack>
			{compareEnabled && compareState.len > 0 ? <CatalogEntryListCompareCollector /> : null}
		</ContentProvider>
	);
};
